import { useAppDispatch } from '@hooks/reduxHooks'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import SaveIcon from '@mui/icons-material/Save'
import ScannerIcon from '@mui/icons-material/Scanner'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { Document } from '@services/api'
import { CreateDocumentDto } from '@services/dtos'
import { DocumentService } from '@services/StorageService'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import moment from 'moment'
import { FC, useCallback, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useIntl } from 'react-intl'
import { FileTypeEnum } from '../../common/interfaces'
import DocumentsScanner from './DocumentsScanner'

const fileTypesToExclude = [
  FileTypeEnum.CHAT_FILE,
  FileTypeEnum.SECONDARY_CAPTURE,
  FileTypeEnum.PROTOCOL_REALISATION,
  FileTypeEnum.SITE_ACQ_PROTOCOL,
]

type Props = {
  onUploaded?: (document: Document) => void
  entityId?: number
  entityName: string
  setUploadedFile: (file: { url: string; mimetype: string } | null) => void
  uploadedFile: { url: string; mimetype: string } | null
}

const DocumentUploader: FC<Props> = ({
  onUploaded,
  entityId,
  entityName,
  uploadedFile,
  setUploadedFile,
}) => {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  // ======================================= STATE =======================================
  const [fileType, setFileType] = useState<FileTypeEnum>(FileTypeEnum.ID)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setisUploading] = useState(false)
  const [scannerModalOpen, setScannerModalOpen] = useState(false)
  const [scannerStatus, setScannerStatus] = useState<
    'idle' | 'scanning' | 'error' | 'ready' | 'searching'
  >('idle')

  // ======================================= FUNCTIONS =======================================
  const uploadFile = useCallback(
    (file: File) => {
      return new Promise<{ url: string; mimetype: string }>(
        (resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('POST', `${process.env.REACT_APP_API_URL}/static`, true)
          xhr.setRequestHeader('Content-Type', 'application/octet-stream')
          xhr.setRequestHeader(
            'Authorization',
            'Bearer ' + localStorage.getItem('authToken'),
          )
          const extension = file.name.split('.').pop()

          xhr.setRequestHeader('x-file-extension', extension || '')
          xhr.setRequestHeader('x-entity-id', entityId?.toString() || '')
          xhr.setRequestHeader('x-entity-name', entityName)
          xhr.responseType = 'json'

          if (fileType) {
            xhr.setRequestHeader('x-file-type', fileType.toString())
          }

          xhr.onload = () => {
            if (xhr.status === 201) {
              const data = xhr.response
              resolve({ url: data.fileUrl, mimetype: file.type })
            }
          }
          xhr.onerror = () => {
            reject(xhr.response)
          }
          xhr.send(file)
        },
      )
    },
    [fileType],
  )

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (file) {
        setisUploading(true)
        try {
          const f = await uploadFile(file)
          setUploadedFile(f)
        } catch (err) {
          console.error(err)
        } finally {
          setisUploading(false)
        }
      }
    },
    [uploadFile, setUploadedFile],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  })

  const handleSaveDocument = async () => {
    if (!uploadedFile) return

    const fileName = uploadedFile.url.split('/').pop()
    if (!fileName) return

    const dto: CreateDocumentDto = {
      url: uploadedFile.url,
      fileType,
      relatedEntity: entityId,
      entityName: entityName,
      filename: fileName,
      mimeType: uploadedFile.mimetype,
    }
    setIsSaving(true)
    try {
      const d = await DocumentService.createDocument(dto)
      onUploaded?.(d)
      dispatch(
        enqueueSnackbar({
          message: 'Document sauvegardé avec succès',
          type: 'success',
        }),
      )
    } catch (error) {
      console.error(error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleScanDocument = async () => {
    setScannerModalOpen(true)
    setScannerStatus('ready')
  }

  const handleScannedFile = async (file: File) => {
    try {
      setisUploading(true)
      const uploadedFileData = await uploadFile(file)
      setUploadedFile(uploadedFileData)
      setScannerModalOpen(false)
      setScannerStatus('idle')

      dispatch(
        enqueueSnackbar({
          message: 'Document scanné avec succès',
          type: 'success',
        }),
      )
    } catch (error) {
      console.error('Upload error:', error)
      dispatch(
        enqueueSnackbar({
          message: 'Erreur lors du téléchargement du document scanné',
          type: 'error',
        }),
      )
    } finally {
      setisUploading(false)
    }
  }

  const renderScannerModal = () => (
    <Modal
      open={scannerModalOpen}
      onClose={() => {
        setScannerModalOpen(false)
        setScannerStatus('idle')
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
          minWidth: 700,
          maxHeight: 800,
          textAlign: 'center',
          overflowY: 'scroll',
        }}
      >
        <DocumentsScanner
          setScannerState={setScannerStatus}
          savePfd={handleScannedFile}
          fileType={fileType}
          setFileType={setFileType}
          fileoptions={fileTypeOptions}
        />
      </Box>
    </Modal>
  )

  const fileTypeOptions = useRef(
    Object.values(FileTypeEnum).filter(
      (v) => fileTypesToExclude.indexOf(v) === -1,
    ),
  ).current

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Stack spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel id="document-type-label">Document Type</InputLabel>
          <Select
            labelId="document-type-label"
            value={fileType}
            label="Document Type"
            onChange={(e) => setFileType(e.target.value as FileTypeEnum)}
            size="small"
          >
            {fileTypeOptions.map((value) => (
              <MenuItem key={value} value={value}>
                {intl.formatMessage({ id: `enums.FileTypeEnum.${value}` })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="column" spacing={1}>
          <Box
            {...getRootProps()}
            sx={{
              flexGrow: 1,
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive
                ? 'action.hover'
                : uploadedFile
                ? 'success.lighter'
                : 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'primary.main',
              },
              minHeight: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <input {...getInputProps()} />
            <Stack spacing={1} alignItems="center">
              {uploadedFile ? (
                <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
              ) : (
                <CloudUploadIcon color="action" sx={{ fontSize: 32 }} />
              )}

              {isUploading ? (
                <CircularProgress size={16} />
              ) : (
                <Typography
                  variant="body2"
                  color={uploadedFile ? 'success.main' : 'text.secondary'}
                >
                  {uploadedFile
                    ? uploadedFile.url.split('/').pop()
                    : isDragActive
                    ? 'Déposez le fichier ici'
                    : 'Cliquez ou déposez un fichier'}
                </Typography>
              )}
            </Stack>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<ScannerIcon />}
            onClick={handleScanDocument}
            sx={{
              height: 'fit-content',
              alignSelf: 'center',
            }}
          >
            Scanner
          </Button>
        </Stack>

        {uploadedFile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                isSaving ? <CircularProgress size={16} /> : <SaveIcon />
              }
              onClick={handleSaveDocument}
              disabled={isSaving}
            >
              {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </Box>
        )}
        {renderScannerModal()}
      </Stack>
    </Paper>
  )
}

export default DocumentUploader
